import React from "react";
import { noop } from "lodash";

import { GearQualityTier, GearUseCase } from "../../../../commons/specs/gear";
import { Component } from "../../../../commons/types/component";
import { IconSource } from "../../../../commons/types/icon";
import {
  IconMediumGravel,
  IconMediumMountain,
  IconMediumRoad,
  IconMediumUrban,
  IconSmallInfo
} from "../../../../resources/icons";
import cn from "../../libs/class-name";
import CheckboxListItem from "../CheckboxListItem/CheckboxListItem";
import Headline from "../Headline/Headline";
import Icon from "../Icon/Icon";

export const gearUseCaseIcons: Record<GearUseCase, IconSource> = {
  mountain: IconMediumMountain,
  road: IconMediumRoad,
  gravel: IconMediumGravel,
  city: IconMediumUrban
};

export interface GearSeriesMatrixCell {
  value: string;
  label: string;
  active?: boolean;
  disabled?: boolean;
}

export interface GearSeriesMatrixColumn {
  key: GearQualityTier;
  label: string;
  subtitle: string;
}

export interface GearSeriesMatrixRow {
  key: GearUseCase;
  icon: IconSource;
  label: string;
  cells: Record<GearQualityTier, GearSeriesMatrixCell[]>;
}

interface Props extends Component {
  header?: React.ReactNode;
  columns: GearSeriesMatrixColumn[];
  rows: GearSeriesMatrixRow[];
  decorative?: boolean;
  decorativeHint?: string;
  onChange?: (value: string) => void;
}

const GearSeriesMatrix = ({
  classNames = [],
  header,
  columns,
  rows,
  decorative = false,
  decorativeHint = "",
  onChange = noop
}: Props) => (
  <div className={cn("GearSeriesMatrix", [{ decorative }], classNames)}>
    {decorative && (
      <div className="GearSeriesMatrix__decorativeHint">
        <Icon source={IconSmallInfo} />
        <span>{decorativeHint}</span>
      </div>
    )}
    {!!header && <div className="GearSeriesMatrix__header">{header}</div>}
    {!decorative && (
      <div className="GearSeriesMatrix__head">
        <div className="GearSeriesMatrix__corner" />
        {columns.map(column => (
          <div className="GearSeriesMatrix__column" key={column.key}>
            <Headline kind="xs" tag="strong">
              {column.label}
            </Headline>
            <span>{column.subtitle}</span>
          </div>
        ))}
      </div>
    )}

    {(decorative ? rows.slice(0, 3) : rows).map((row, rowIndex) => (
      <div className="GearSeriesMatrix__row" key={row.key}>
        {!decorative && (
          <div className="GearSeriesMatrix__rowLabel" aria-label={row.label}>
            <Icon source={row.icon} size="l" />
            <Headline kind="xs" tag="span">
              {row.label}
            </Headline>
          </div>
        )}
        {columns.map((column, columnIndex) => {
          const series = row.cells[column.key];

          if (decorative && rowIndex === 2 && columnIndex > 0) {
            return null;
          }

          return (
            <div
              className={cn("GearSeriesMatrix__cell", [{ empty: series.length === 0 }])}
              key={`${row.key}-${column.key}`}
              aria-hidden={decorative || undefined}
            >
              {decorative ? (
                <>
                  <span className="GearSeriesMatrix__skeleton" />
                  <span className="GearSeriesMatrix__skeletonCheckbox" />
                </>
              ) : series.length === 0 ? (
                <span className="GearSeriesMatrix__empty">—</span>
              ) : (
                series.map(item => (
                  <CheckboxListItem
                    key={item.value}
                    outline
                    label={item.label}
                    active={item.active}
                    disabled={item.disabled}
                    onClick={() => onChange(item.value)}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    ))}
  </div>
);

export default GearSeriesMatrix;
